import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthenticationService } from '../../../services/authentication.service';
import { HelpersService } from '../../../services/helpers.service';
import { UserService } from '../../../services/user.service';
import { PartnerService } from '../../../services/partner.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/app.config';
import { state } from '@angular/animations';

@Component({
  selector: 'app-trek-gigya-login',
  templateUrl: './trek-gigya-login.component.html',
  styleUrls: ['./trek-gigya-login.component.scss']
})
export class TrekGigyaLoginComponent {

  public loggingIn: boolean = false;
  public error: boolean = false;
  public errorMessage: string = '';
  public returnURL: string = null;
  public gigyaInitialized = true;
  public isAuthInProgress = false;
  public isCallbackRoute = false;
  private gigyaConfig: any;
  public showControl : boolean = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    public authenticationService: AuthenticationService,
    public userService: UserService,
    public partnerService: PartnerService,
    public helpersService: HelpersService,
    public router: Router,
    public route: ActivatedRoute,
    private appConfig: AppConfig
  ) {
    this.gigyaConfig = this.appConfig.getGigyaConfig();
  }


  async ngOnInit() {
    this.showControl = window.location.href.includes('localhost') || window.location.href.includes('workorder.dnecsarms.com') || window.location.href.includes('.ascendrms.com');
    var code = null;
    var state = null;
    this.route.queryParams.subscribe(params => {
      this.returnURL = params['returnUrl'];
      code = params['code'];
      state = params['state'];
      this.errorMessage = params['error'];
      if(this.errorMessage){
        this.error = true;
      }
    });
    this.isCallbackRoute = this.router.url.startsWith('/auth/callback/ascend');


    if (code) {
      console.log('Authorization code received from Gigya:', code);
      console.log('State:', state);

      // Get the code_verifier we stored BEFORE the redirect
      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');

      if (codeVerifier) {
        console.log('Code verifier found in storage');

        // Clean up the URL (remove hash)
        window.history.replaceState({}, document.title, window.location.pathname);

        this.handleAuthorizationCode(code, codeVerifier);

        // Clear the verifier after use
        sessionStorage.removeItem('pkce_code_verifier');
        sessionStorage.removeItem('pkce_state');
      } else {
        console.error('Code verifier not found in storage');
        this.error = true;
        this.errorMessage = 'Authentication state not found. Please try again.';
        this.redirectToHome(this.errorMessage);
      }
      return;
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  redirectToHome(errorMessage: string) {
    this.router.navigateByUrl('/?error=' + encodeURIComponent(errorMessage) || 'Unknown error');
  }

  /**
   * Generate a cryptographically random string for PKCE
   */
  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    let result = '';
    for (let i = 0; i < length; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    return result;
  }

  /**
   * Generate SHA256 hash and base64url encode it
   */
  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await crypto.subtle.digest('SHA-256', data);

    // Convert to base64url
    return this.base64UrlEncode(new Uint8Array(hash));
  }

  /**
   * Base64URL encode
   */
  private base64UrlEncode(buffer: Uint8Array): string {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  async loginToGigya() {
    if (this.isAuthInProgress) {
      console.log('Authentication already in progress, please wait...');
      return;
    }    

    this.loggingIn = true;
    this.isAuthInProgress = true;
    this.error = false;
    this.errorMessage = '';

    try {
      // Generate PKCE parameters
      const codeVerifier = this.generateRandomString(128);
      const codeChallenge = await this.generateCodeChallenge(codeVerifier);
      const state = this.generateRandomString(32);

      // Store code_verifier and state in sessionStorage
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      sessionStorage.setItem('pkce_state', state);

      // Build authorization URL for Gigya
      const redirectUri = window.location.origin + "/auth/callback/ascend";
      const scope = 'openid email asc_permissions';

      const authUrl = `${this.gigyaConfig.baseUrl}/${this.gigyaConfig.apiKey}/authorize?` +
        `client_id=${this.gigyaConfig.clientId}` +
        `&response_type=code` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_mode=fragment` +
        `&scope=${encodeURIComponent(scope)}` +
        `&state=${state}` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256`;

      console.log('Redirecting to Gigya:', authUrl);

      // Redirect to Gigya
      window.location.href = authUrl;

    } catch (error) {
      console.error('Gigya login failed:', error);
      this.loggingIn = false;
      this.isAuthInProgress = false;
      this.error = true;
      this.errorMessage = 'SSO login failed. Please try again.';
    }
  }

  private handleAuthorizationCode(code: string, codeVerifier: string) {
    this.loggingIn = true;
    this.isAuthInProgress = true;

    console.log('Sending to backend:');
    console.log('  - Authorization code (first 20 chars):', code.substring(0, 20));
    console.log('  - Code verifier (first 20 chars):', codeVerifier.substring(0, 20));
    const redirectUri = window.location.origin + "/auth/callback/ascend";
    // Send the authorization code AND code_verifier to your backend
    this.authenticationService.authenticateWithGigyaCode(code, codeVerifier, redirectUri)
      .subscribe(
        (authResult) => {
          // Backend returns your standard auth tokens
          this.handleGigyaLoginSuccess(authResult);
        },
        (error) => {
          console.error('Backend authentication failed:', error);
          this.loggingIn = false;
          this.isAuthInProgress = false;
          this.error = true;
          this.errorMessage = error?.error?.message || 'SAP Gigya authentication failed. Please try again.';
          this.redirectToHome(this.errorMessage);
        }
      );
  }

  private async handleGigyaLoginSuccess(authResult: any) {
    try {
      this.loggingIn = true;
      this.isAuthInProgress = true;

      // Save tokens from your backend
      this.helpersService.saveAuthData(authResult);
      this.helpersService.saveAuthDataNew(authResult);

      // Get user profile
      this.userService.getUserDetail(authResult.userName).subscribe(
        async (user) => {
          const profile = user;
          const roles = profile.UserRoles.filter((x) => x.PartnerID > 0);

          var shops = roles
            .map((x) => x.PartnerID)
            .filter((value, index, self) => self.indexOf(value) === index);

          if (shops.length > 0) {
            var defaultPartnerID = shops[0];
            profile.PartnerID = defaultPartnerID;

            var shopRoles = roles
              .filter((x) => x.PartnerID === defaultPartnerID)
              .map((x) => x.RoleID);

            profile.isAdmin = shopRoles.includes(2) || profile.UserRoles.includes(3);
            profile.isManager = shopRoles.includes(3);
            profile.isServiceLead = shopRoles.includes(9);
            profile.isTechnician =
              (shopRoles.includes(1) || shopRoles.includes(4)) &&
              !profile.isAdmin &&
              !profile.isManager &&
              !profile.isServiceLead;
          }

          this.helpersService.saveProfileData(profile);

          this.userService.getUserRolesInsurance(profile.ID).subscribe(
            (permissions) => {
              this.helpersService.saveProfileInsuranceRoles(permissions);
            },
            (error) => {
              console.error('Error loading insurance roles:', error);
            }
          );

          // Navigate based on user role
          if (this.returnURL) {
            this.router.navigateByUrl(this.returnURL);
          } else if (profile.IsSuperUser) {
            this.router.navigateByUrl('/administration/stores');
          } else if (profile.isAdmin || profile.isManager || profile.isTechnician || profile.isServiceLead) {
            this.router.navigateByUrl('/store/product-redirect-loader');
          } else {
            this.error = true;
            this.errorMessage = 'User does not have a valid role assigned to any store.';
            this.isAuthInProgress = false;
          }

          this.loggingIn = false;
          this.isAuthInProgress = false;
        },
        (error) => {
          console.error('Error loading user profile:', error);
          this.loggingIn = false;
          this.isAuthInProgress = false;
          this.error = true;
          this.errorMessage = 'Failed to load user profile. Please try again.';
        }
      );
    } catch (error) {
      console.error('Error handling Gigya login:', error);
      this.loggingIn = false;
      this.isAuthInProgress = false;
      this.error = true;
      this.errorMessage = 'Authentication failed. Please try again.';
    }
  }

  private parseHashFragment(hash: string): { [key: string]: string } {
    const params: { [key: string]: string } = {};

    if (!hash || hash.length === 0) {
      return params;
    }

    // Remove the leading '#' if present
    const hashContent = hash.startsWith('#') ? hash.substring(1) : hash;

    // Split by '&' to get individual parameters
    const pairs = hashContent.split('&');

    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key && value) {
        params[key] = decodeURIComponent(value);
      }
    }

    return params;
  }
}
